import React from 'react';
import { Box, Typography, Chip, Paper, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PatientBoardConfig, PatientCardVM, phaseStyle } from './types';

const PatientCard: React.FC<{ patient: PatientCardVM; config: PatientBoardConfig }> = ({ patient, config }) => {
  const navigate = useNavigate();
  const statusStyle = phaseStyle(config, patient.phaseKey);

  return (
    <Paper
      variant="outlined"
      onClick={() => navigate(`${config.routeBase}/patient/${patient.id}`)}
      sx={{
        p: 1.5,
        cursor: 'pointer',
        borderLeft: '3px solid',
        borderLeftColor: patient.urgency?.borderColor ?? 'divider',
        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
        transition: 'border-color 0.15s ease',
      }}
    >
      <Typography variant="body2" fontWeight={600} noWrap>{patient.name}</Typography>
      <Typography variant="caption" color="text.secondary">Age {patient.age} · {patient.locationSecondary}</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
        {patient.admitDate ? `Admit ${patient.admitDate} · ` : ''}Exp. {patient.expectedDischarge}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
        <Chip
          label={patient.scoreCell.label}
          size="small" variant="outlined"
          sx={{ fontSize: '0.65rem', height: 18 }}
        />
        {patient.urgency && (
          <Chip
            label={patient.urgency.label}
            size="small"
            sx={{ bgcolor: patient.urgency.bgcolor, color: patient.urgency.color, fontWeight: 600, fontSize: '0.65rem', height: 18 }}
          />
        )}
      </Stack>
      <Box sx={{ mt: 0.75 }}>
        <Chip
          label={statusStyle.label}
          size="small"
          sx={{ bgcolor: statusStyle.bgcolor, color: statusStyle.color, fontWeight: 600, fontSize: '0.65rem', height: 18 }}
        />
      </Box>
    </Paper>
  );
};

interface Props {
  patients: PatientCardVM[];
  config: PatientBoardConfig;
}

const PatientBoardKanban: React.FC<Props> = ({ patients, config }) => {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', overflowX: 'auto', pb: 2 }}>
      {config.phases.map(col => {
        const colPatients = patients.filter(p => p.phaseKey === col.key);
        return (
          <Box key={col.key} sx={{ minWidth: 220, maxWidth: 240, flex: '0 0 auto' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Typography
                variant="overline" color="primary.main" fontWeight={700}
                sx={{ letterSpacing: '0.08em', lineHeight: 1 }}
              >
                {col.label}
              </Typography>
              <Chip
                label={colPatients.length}
                size="small" variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            </Stack>
            <Stack spacing={1}>
              {colPatients.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderStyle: 'dashed' }}>
                  <Typography variant="caption" color="text.disabled">None</Typography>
                </Paper>
              ) : (
                colPatients.map(p => <PatientCard key={p.id} patient={p} config={config} />)
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export default PatientBoardKanban;
